import React from 'react';
import { Info, ListChecks, RefreshCw } from 'lucide-react';
import { RegulationLink } from '../ui/RegulationLink';
type FoodClaimVerdict = 'Go' | 'Review' | 'Not permitted';
type FoodClaim = {
  text: string;
  type: string;
  verdict: FoodClaimVerdict;
  reference: string;
  recommendation?: string;
};
const claims: FoodClaim[] = [{
  text: 'Formulated Supplementary Food',
  type: 'Presentation',
  verdict: 'Not permitted',
  reference: 'Standard 2.9.3 Formulated meal replacements and formulated supplementary foods',
  recommendation: 'The label declares the product a formulated supplementary food, which invokes Standard 2.9.3 and its compositional and labelling requirements, but the dossier classifies it as a general food in the chocolate and cocoa category. Resolve the discrepancy before proceeding.'
}, {
  text: 'LOW GI = 36 (Low) made with skim milk',
  type: 'Nutrition',
  verdict: 'Review',
  reference: 'Standard 1.2.7 Nutrition, health and related claims',
  recommendation: "Substantiate the glycaemic index value by an accredited method and carry the 'made with skim milk' qualification with the claim wherever it appears."
}, {
  text: 'Vitamin and mineral content declarations',
  type: 'Nutrition',
  verdict: 'Review',
  reference: 'Standard 1.2.7 Nutrition, health and related claims',
  recommendation: 'Declaring these in the nutrition panel is permitted. Presenting them front-of-pack becomes a nutrition content claim, and any associated health claim is blocked by the NPSC score.'
}, {
  text: 'Contains Gluten, Milk, Soy',
  type: 'Mandatory',
  verdict: 'Go',
  reference: 'Standard 1.2.3 Information requirements — warning statements, advisory statements and declarations',
  recommendation: 'Allergen declaration present and correctly worded.'
}, {
  text: 'Total Extract 38%',
  type: 'Mandatory',
  verdict: 'Go',
  reference: 'Standard 1.2.1 Requirements to have labels or otherwise provide information',
  recommendation: 'Characterising ingredient percentage declared.'
}, {
  text: 'Made with skim milk',
  type: 'Voluntary',
  verdict: 'Go',
  reference: 'Standard 1.2.7 Nutrition, health and related claims'
}];
export function FoodClaimsTab() {
  return <div className="space-y-5" data-id="element-157" data-mp="3vcrgte17jfy7">
      <header className="flex items-start gap-3" data-id="element-158" data-mp="3vdt4chzetuwt">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#eff3ed] text-forest-green" data-id="element-159" data-mp="37mx3kvcz7pdf">
          <ListChecks className="h-5 w-5" data-id="element-160" data-mp="33mwpxcxpbi7f" />
        </span>
        <div data-id="element-161" data-mp="3hlmpr0bwnvzh">
          <h2 className="font-[Figtree] text-2xl font-semibold text-evergreen" data-id="element-162" data-mp="3hm5utgggipw3">Claims</h2>
          <p className="mt-1 text-sm leading-relaxed text-[#6b7d70]" data-id="element-163" data-mp="3cmt8qxxetmuk">Claims extracted from the label and assessed against the FSANZ Food Standards Code.</p>
        </div>
      </header>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-164" data-mp="wx8eon4ndhxw">
        <p className="text-sm text-[#718174]" data-id="element-165" data-mp="ryf2cy13jwzb">6 claims extracted from 3 label faces · analysed 7 Jul 2026</p>
        <button type="button" className="inline-flex shrink-0 items-center gap-1.5 self-start rounded-lg border border-[#d6dfd3] bg-white px-3 py-2 text-sm font-semibold text-evergreen transition hover:bg-[#f7faf5] sm:self-auto" data-id="element-166" data-mp="29wa5kmwczc5o">
          <RefreshCw className="h-4 w-4" data-id="element-167" data-mp="25svsa3zt19vx" />
          Re-analyse
        </button>
      </div>

      <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-relaxed text-amber-900" data-id="element-168" data-mp="2tj7x35zr6qia">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" data-id="element-169" data-mp="2xlcr45u45p5g" />
        Health claims are currently blocked. The NPSC score of 15 exceeds the threshold of 4.0, so no general or high-level health claim may be made until the product is reformulated.
      </div>

      <div className="space-y-4" data-id="element-170" data-mp="3cf7skqrxwc6m">
        {claims.map(claim => <article key={claim.text} className="rounded-2xl border border-[#dce3da] bg-white p-5 shadow-sm" data-id="element-171" data-mp="3h7r4ljazaz9b">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between" data-id="element-172" data-mp="1smr5aapt1mak">
              <h3 className="font-[Figtree] text-lg font-semibold text-evergreen" data-id="element-173" data-mp="1nljyoezeet5h">{claim.text}</h3>
              <div className="flex shrink-0 items-center gap-2" data-id="element-174" data-mp="1jm1ddi3o0g24">
                <span className="rounded-full bg-[#eef1ec] px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-[#8b968d]" data-id="element-175" data-mp="1elwyfwiente0">{claim.type}</span>
                <VerdictPill verdict={claim.verdict} data-id="element-176" data-mp="2bbed30zd8pru" />
              </div>
            </div>
            <div className="mt-5" data-id="element-177" data-mp="27bdwpaspi4i5">
              <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-[#7a897d]" data-id="element-178" data-mp="22jvu6ib1bvv2">Regulatory reference</p>
              <p className="mt-1 text-sm font-medium text-evergreen" data-id="element-179" data-mp="1xjpz0s5k3r88">{claim.reference}</p>
              {claim.recommendation && <div className="mt-4" data-id="element-180" data-mp="2v1hp0m0g2htu">
                  <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-[#7a897d]" data-id="element-181" data-mp="2q9q52ubsnaco">Recommendation</p>
                  <p className="mt-1 text-sm leading-relaxed text-[#506355]" data-id="element-182" data-mp="2xlmofme7ceri">{claim.recommendation}</p>
                </div>}
              <div className="mt-4" data-id="element-183" data-mp="32lsbep9j1d39">
                <RegulationLink text={claim.reference} data-id="element-184" data-mp="37i97q6qvqxrf" />
              </div>
            </div>
          </article>)}
      </div>
    </div>;
}
function VerdictPill({
  verdict
}: {
  verdict: FoodClaimVerdict;
}) {
  const style: Record<FoodClaimVerdict, string> = {
    Go: 'bg-forest-green/10 text-forest-green',
    Review: 'bg-amber-100 text-amber-800',
    'Not permitted': 'bg-red-100 text-red-800'
  };
  const label: Record<FoodClaimVerdict, string> = {
    Go: 'Permitted',
    Review: 'Permitted with conditions',
    'Not permitted': 'Not permitted'
  };
  return <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${style[verdict]}`} data-id="element-185" data-mp="3va6bdak6ejhg">{label[verdict]}</span>;
}